import { seatAngle } from '@spin/shared';

/** The board's size in the reference (CSS px at a 412 px wide phone); everything inside scales with its width. */
export const BOARD = { width: 412, height: 460 };

/** Where the bottle stands, in % of the board. */
export const BOTTLE_CENTER = { left: 50, top: 55.6 };

/**
 * Seat centres of the 12-seat table in % of the board, measured from the reference: four on top, two on each side,
 * four at the bottom. Seat 0 is the first one right of 12 o'clock and the seats go clockwise, so seat i sits at
 * about 15° + 30°·i around the bottle — half a sector after `seatAngle`, see `drawnAngle`.
 */
const CLASSIC_12 = [
  { left: 61.0, top: 21.9 },
  { left: 82.6, top: 26.9 },
  { left: 86.9, top: 46.3 },
  { left: 86.9, top: 65.5 },
  { left: 82.8, top: 84.9 },
  { left: 61.0, top: 89.3 },
  { left: 39.2, top: 89.3 },
  { left: 17.2, top: 84.9 },
  { left: 13.1, top: 65.5 },
  { left: 13.1, top: 46.3 },
  { left: 17.4, top: 26.9 },
  { left: 39.2, top: 21.9 },
];

/** The two players of a round meet in the middle (spinner left, partner right). */
export const PAIR_POSITIONS = [
  { left: 37.5, top: 53.5 },
  { left: 62.5, top: 53.5 },
];

/**
 * The angle at which seat `seat` is drawn (degrees, clockwise from 12 o'clock). The server aims the bottle at
 * `seatAngle`; the board draws every seat — and so the bottle — half a sector further.
 */
export function drawnAngle(seat: number, capacity: number): number {
  return seatAngle(seat, capacity) + 180 / capacity;
}

/** The bottle's on-screen rotation for an absolute server angle. */
export function bottleRotation(angle: number, capacity: number): number {
  return angle + 180 / capacity;
}

/** Seat centre in % of the board. Other table sizes fall back to an ellipse around the bottle. */
export function seatPosition(seat: number, capacity: number): { left: number; top: number } {
  if (capacity === CLASSIC_12.length) return CLASSIC_12[seat]!;
  const a = (drawnAngle(seat, capacity) * Math.PI) / 180;
  return { left: 50 + 37 * Math.sin(a), top: BOTTLE_CENTER.top - 33 * Math.cos(a) };
}

/** Whole seconds left until `deadline` (server time), never negative. */
export function secondsLeft(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** The 4-digit table number shown in the top bar ("Table 4446"), derived from the room id. */
export function tableNumber(roomId: string): number {
  let h = 0;
  for (const ch of roomId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return 1000 + (h % 9000);
}
